import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { leagues, seasons, organizations } from '../lib/api'
import { getOrganizationSlugFromSubdomain } from '../lib/utils'
import { useUser } from './UserContext'

const LeagueContext = createContext(null)

// Flag to prevent duplicate fetchLeagueData calls
let isFetchingLeagueData = false


/**
 * LeagueContext provides centralized league/season state management
 * Key features:
 * - Auto-detects league from subdomain
 * - For single-league orgs, hides org complexity
 * - Tracks current season with auto-selection
 * - Provides breadcrumb data for navigation
 */
export function LeagueProvider({ children }) {
  const { user, isAuthenticated } = useUser()

  // Core state
  const [currentOrg, setCurrentOrg] = useState(null)
  const [currentLeague, setCurrentLeague] = useState(null)
  const [currentSeason, setCurrentSeason] = useState(null)
  const [allLeagues, setAllLeagues] = useState([])
  const [allSeasons, setAllSeasons] = useState([])

  // Computed state
  const [isSingleLeagueOrg, setIsSingleLeagueOrg] = useState(false)
  const [loading, setLoading] = useState(true)

  // Use ref to track currentLeague without adding it to dependencies
  const currentLeagueRef = useRef(currentLeague)
  useEffect(() => {
    currentLeagueRef.current = currentLeague
  }, [currentLeague])

  // Fetch organization and leagues based on subdomain
  const fetchLeagueData = useCallback(async () => {
    // Prevent concurrent calls
    if (isFetchingLeagueData) {
      return
    }

    if (!isAuthenticated) {
      setLoading(false)
      return
    }

    isFetchingLeagueData = true

    try {
      const subdomain = getOrganizationSlugFromSubdomain()

      // Get all orgs user has access to
      const orgs = await organizations.getAll()

      // Get all leagues
      const leaguesList = await leagues.getAll()
      setAllLeagues(leaguesList)

      // Find org from subdomain
      let org = null
      if (subdomain && subdomain !== 'admin') {
        org = orgs.find(o => o.slug === subdomain)
      } else if (orgs.length === 1) {
        // Single org user - auto-select
        org = orgs[0]
      }

      if (org) {
        setCurrentOrg(org)

        // Get leagues for this org
        const orgLeagues = leaguesList.filter(l =>
          l.organization_id === org.id && l.archived !== 1
        )

        // Check if single-league org
        setIsSingleLeagueOrg(orgLeagues.length === 1)

        // Auto-select league if single-league org (use ref to avoid dependency loop)
        if (orgLeagues.length === 1 && !currentLeagueRef.current) {
          const league = orgLeagues[0]
          setCurrentLeague(league)

          // Fetch seasons for this league
          try {
            const leagueSeasons = await seasons.getByLeague(league.id)
            setAllSeasons(leagueSeasons)

            // Auto-select active season
            const activeSeason = leagueSeasons.find(s => s.is_active === 1 && s.archived !== 1)
            if (activeSeason) {
              setCurrentSeason(activeSeason)
            } else if (leagueSeasons.length > 0) {
              const firstSeason = leagueSeasons.find(s => s.archived !== 1) || leagueSeasons[0]
              setCurrentSeason(firstSeason)
            }
          } catch (err) {
            console.error('Error fetching seasons:', err)
          }
        }
      }
    } catch (error) {
      console.error('Error fetching league data:', error)
    } finally {
      setLoading(false)
      isFetchingLeagueData = false
    }
  }, [isAuthenticated])

  // Select a different league
  const selectLeague = useCallback(async (league) => {
    setCurrentLeague(league)
    setCurrentSeason(null)

    if (league) {
      try {
        const leagueSeasons = await seasons.getByLeague(league.id)
        setAllSeasons(leagueSeasons)

        // Auto-select active season
        const activeSeason = leagueSeasons.find(s => s.is_active === 1 && s.archived !== 1)
        if (activeSeason) {
          setCurrentSeason(activeSeason)
        } else if (leagueSeasons.length > 0) {
          const firstSeason = leagueSeasons.find(s => s.archived !== 1) || leagueSeasons[0]
          setCurrentSeason(firstSeason)
        }
      } catch (err) {
        console.error('Error fetching seasons for league:', err)
      }
    }
  }, [])

  // Select a different season
  const selectSeason = useCallback((season) => {
    setCurrentSeason(season)
  }, [])

  // Build breadcrumb data
  const breadcrumbs = useMemo(() => {
    const crumbs = []

    // Only show org if multi-league org
    if (currentOrg && !isSingleLeagueOrg) {
      crumbs.push({
        label: currentOrg.name,
        path: '/dashboard',
        type: 'org'
      })
    }

    if (currentLeague) {
      crumbs.push({
        label: currentLeague.name,
        path: currentLeague.slug ? `/${currentLeague.slug}` : `/leagues/${currentLeague.id}`,
        type: 'league'
      })
    }

    if (currentSeason) {
      crumbs.push({
        label: currentSeason.name,
        type: 'season'
      })
    }

    return crumbs
  }, [currentOrg, currentLeague, currentSeason, isSingleLeagueOrg])

  // Get leagues for current org
  const orgLeagues = useMemo(() => {
    if (!currentOrg) return allLeagues
    return allLeagues.filter(l => l.organization_id === currentOrg.id && l.archived !== 1)
  }, [currentOrg, allLeagues])

  // Initialize on mount when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchLeagueData()
    }
  }, [isAuthenticated, fetchLeagueData])

  // Listen for league changes (e.g., from SetupWizard)
  useEffect(() => {
    const handleLeagueChange = () => {
      fetchLeagueData()
    }
    window.addEventListener('leaguesChanged', handleLeagueChange)
    return () => window.removeEventListener('leaguesChanged', handleLeagueChange)
  }, [fetchLeagueData])

  const value = useMemo(() => ({
    // Current selections
    currentOrg,
    currentLeague,
    currentSeason,

    // Lists
    allLeagues,
    allSeasons,
    orgLeagues,

    // Computed
    isSingleLeagueOrg,
    breadcrumbs,
    loading,

    // Actions
    selectLeague,
    selectSeason,
    setCurrentLeague,
    setCurrentSeason,
    refreshLeagues: fetchLeagueData,
  }), [
    currentOrg,
    currentLeague,
    currentSeason,
    allLeagues,
    allSeasons,
    orgLeagues,
    isSingleLeagueOrg,
    breadcrumbs,
    loading,
    selectLeague,
    selectSeason,
    fetchLeagueData,
  ])

  return <LeagueContext.Provider value={value}>{children}</LeagueContext.Provider>
}

export function useLeague() {
  const context = useContext(LeagueContext)
  if (!context) {
    throw new Error('useLeague must be used within a LeagueProvider')
  }
  return context
}

export default LeagueContext
